import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { ANIMATION_REGISTRY } from '@/config/animationRegistry';
import { cx } from './cx';

export interface SkeletonProps {
  className?: string;
  style?: CSSProperties;
}

const { shimmer } = ANIMATION_REGISTRY;

/** Cosmos skeleton — L→R shimmer at a shared px/s (wide Games chips stay in sync with Wallet). */
export default function Skeleton({ className, style }: SkeletonProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [durationMs, setDurationMs] = useState(shimmer.durationMs);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const apply = () => {
      const w = el.getBoundingClientRect().width;
      if (w <= 0) return;
      const next = Math.max(
        shimmer.minDurationMs,
        Math.round(((w + shimmer.bandPx) / shimmer.pxPerSec) * 1000)
      );
      setDurationMs((prev) => (prev === next ? prev : next));
    };

    const ro = new ResizeObserver(apply);
    ro.observe(el);
    apply();
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      aria-hidden
      className={cx('cosmos-skeleton rounded-control bg-white/8 overflow-hidden relative', className)}
      style={style}
    >
      <div
        className={shimmer.className}
        style={
          {
            '--cosmos-shimmer-duration': `${durationMs}ms`,
            '--cosmos-shimmer-band': `${shimmer.bandPx}px`,
          } as CSSProperties
        }
      />
    </div>
  );
}
